import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Users, Eye, Globe2, RefreshCw, Home, Bot, Cpu } from "lucide-react";
import { api } from "@/lib/api";
import BotTrafficInsights from "@/components/admin/BotTrafficInsights";
import UserActivityLog from "@/components/admin/UserActivityLog";
import RoniUsagePanel from "@/components/admin/RoniUsagePanel";
import AnalyticsCleanup from "@/components/admin/AnalyticsCleanup";
import { useAuth } from "@/context/AuthContext";

const RANGES = [
  { key: "today", label: "Today", short: "Today" },
  { key: "yesterday", label: "Yesterday", short: "Yesterday" },
  { key: "last_week", label: "Last 7 days", short: "7D" },
  { key: "last_month", label: "Last 30 days", short: "30D" },
  { key: "all", label: "All time", short: "All" },
];

export default function AdminStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("last_week");
  const { user } = useAuth();
  // Backend `require_owner` accepts role === "admin" (advisors are locked
  // out). Advisors would get a 403 from the cleanup endpoint anyway, but
  // hiding the panel avoids the empty-state / error-state noise.
  const isOwner = user?.role === "admin";

  const load = async (r = range) => {
    setLoading(true);
    try {
      const res = await api.get("/stats/summary", { params: { range: r } });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(range);
    const iv = setInterval(() => load(range), 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const flag = (country) => {
    const M = { "United Arab Emirates": "ae", "United Kingdom": "gb", "United States": "us", "Saudi Arabia": "sa", "India": "in", "Pakistan": "pk", "Canada": "ca", "Australia": "au", "France": "fr", "Germany": "de", "Russia": "ru", "Egypt": "eg", "Jordan": "jo", "Lebanon": "lb", "Kuwait": "kw", "Qatar": "qa", "Oman": "om", "Bahrain": "bh", "Turkey": "tr", "China": "cn", "Nigeria": "ng", "South Africa": "za", "Netherlands": "nl", "Spain": "es", "Italy": "it", "Singapore": "sg" };
    return M[country] || null;
  };

  const users = data?.users || {};
  const bots = data?.bots || {};
  const clicks = data?.clicks_by_type || {};

  return (
    <div data-testid="admin-stats-page">
      <div className="mb-6">
        <p className="font-mono-brand text-propertic-gold mb-2 text-[10px] sm:text-xs tracking-[0.14em] uppercase">Statistics</p>
        <h1 className="font-editorial text-3xl sm:text-4xl text-propertic-navy">Traffic & call intent</h1>
        <p className="text-propertic-muted text-xs sm:text-sm mt-2 max-w-2xl">
          Split into humans (Users) and search / AI crawlers (Bot Crawls) so you can measure GEO
          adoption after the Bing + ChatGPT + Claude push.
        </p>
      </div>

      {/* Filter chips + refresh — mobile-safe wrap */}
      <div className="mb-8 flex flex-wrap items-center gap-2" data-testid="stats-controls">
        <div
          className="flex flex-wrap items-center gap-1 bg-white rounded-2xl border hairline p-1 max-w-full"
          data-testid="stats-range-filter"
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`stats-range-${r.key}`}
              className={`h-8 px-3 rounded-xl font-mono-brand text-[10px] tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${range === r.key ? "bg-propertic-navy text-white" : "text-propertic-muted hover:text-propertic-navy"}`}
            >
              <span className="sm:hidden">{r.short}</span>
              <span className="hidden sm:inline">{r.label}</span>
            </button>
          ))}
        </div>
        <button
          onClick={() => load(range)}
          disabled={loading}
          data-testid="stats-refresh"
          className="h-9 px-3 rounded-full border hairline text-[10px] font-mono-brand tracking-[0.12em] uppercase text-propertic-navy hover:border-propertic-gold hover:text-propertic-gold inline-flex items-center gap-2 disabled:opacity-60"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && !data && <p className="text-sm text-propertic-muted">Loading…</p>}
      {data && (
        <>
          {/* ============================================================
              USERS section (humans only) — one number per card, scoped
              to the selected range. Add-Roni-to-Home lives here because
              it's a human-triggered install action, not a bot crawl.
              ============================================================ */}
          <div className="mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-propertic-navy" />
            <h2 className="font-editorial text-lg text-propertic-navy">Users · {data.range_label}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <StatCard icon={Users} label="Unique users" value={users.unique_range || 0} range={data.range_label} testid="stat-users-uniques" />
            <StatCard icon={Eye} label="Pageviews" value={users.pageviews_range || 0} range={data.range_label} testid="stat-users-pageviews" />
            <StatCard icon={MessageCircle} label="WhatsApp clicks" value={clicks.whatsapp || 0} range={data.range_label} testid="stat-whatsapp" accent="#25D366" to="/admin/stats/clicks/whatsapp" />
            <StatCard icon={Phone} label="Call clicks" value={clicks.call || 0} range={data.range_label} testid="stat-call" accent="#B08543" to="/admin/stats/clicks/call" />
            <StatCard icon={Home} label="Add-Roni-to-Home clicks" value={clicks.roni_install_cta || 0} range={data.range_label} testid="stat-roni-install" accent="#B08543" to="/admin/stats/clicks/roni_install_cta" />
          </div>

          {/* ============================================================
              BOT CRAWLS section (search + AI engines only)
              ============================================================ */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Bot className="w-4 h-4 text-propertic-gold" />
            <h2 className="font-editorial text-lg text-propertic-navy">Bot Crawls · {data.range_label}</h2>
            <span className="text-[10px] sm:text-[11px] text-propertic-muted font-mono-brand">tracks ChatGPT · Claude · Perplexity · Gemini · Bing · Google</span>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
            <StatCard icon={Cpu} label="Unique bots" value={bots.unique_range || 0} range={data.range_label} testid="stat-bots-uniques" accent="#B08543" />
            <StatCard icon={Bot} label="Bot pageviews" value={bots.pageviews_range || 0} range={data.range_label} testid="stat-bots-pageviews" accent="#B08543" />
          </div>

          {(bots.top_bots || []).length > 0 && (
            <section className="bg-white rounded-2xl border hairline p-5 sm:p-6 mb-8" data-testid="top-bots-card">
              <h3 className="font-editorial text-base sm:text-lg text-propertic-navy mb-4">Top crawlers · {data.range_label}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {(bots.top_bots || []).map((b) => {
                  const max = bots.top_bots[0].count || 1;
                  const pct = Math.max(4, Math.round((b.count / max) * 100));
                  const isAI = /GPT|Claude|Perplexity|anthropic|Google-Extended|Applebot-Extended|CCBot|cohere|Amazon|Bytespider|Meta-External/i.test(b.bot);
                  return (
                    <div key={b.bot} className="flex items-center gap-3" data-testid={`bot-${b.bot}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-sm text-propertic-navy truncate flex items-center gap-1.5">
                            {isAI && <Cpu className="w-3 h-3 text-propertic-gold flex-shrink-0" />}
                            <span className="truncate">{b.bot}</span>
                          </p>
                          <p className="font-mono-brand text-[11px] text-propertic-muted tabular">{b.count}</p>
                        </div>
                        <div className="h-1.5 bg-propertic-cream rounded-full overflow-hidden">
                          <div className={`h-full ${isAI ? "bg-gradient-to-r from-propertic-gold to-propertic-gold-2" : "bg-gradient-to-r from-propertic-navy to-propertic-navy-2"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* ============================================================
              USER ACTIVITY LOG — human pageviews with source attribution
              (Google Search / Google Ads / Bing / ChatGPT / Direct etc.)
              Mounted directly above the bot cards per Rani's request.
              ============================================================ */}
          <RoniUsagePanel range={range} />
          <UserActivityLog range={range} rangeLabel={data.range_label} />

          {/* ============================================================
              BOT TRAFFIC INSIGHTS — category rollup + first/last-seen
              ============================================================ */}
          <BotTrafficInsights range={range} rangeLabel={data.range_label} />

          {/* ============================================================
              ANALYTICS CLEANUP — owner only. Re-runs bot detection over
              a rolling window and rebuilds daily counters so the summary
              cards reflect real humans + correctly-labelled bots only.
              ============================================================ */}
          {isOwner && <AnalyticsCleanup onDone={() => load(range)} />}

          {/* ============================================================
              Daily series + top countries — mobile-safe grid
              ============================================================ */}
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-4 sm:gap-6 mb-8">
            <section className="bg-white rounded-2xl border hairline p-5 sm:p-6 overflow-hidden" data-testid="daily-series-card">
              <h2 className="font-editorial text-base sm:text-xl text-propertic-navy mb-4">Last 14 days · users vs bots</h2>
              <DailyBars series={data.daily_series || []} />
              <div className="flex items-center gap-4 mt-4 justify-center flex-wrap">
                <span className="flex items-center gap-2 text-[11px] text-propertic-muted font-mono-brand"><span className="w-3 h-3 rounded-sm bg-gradient-to-t from-propertic-navy to-propertic-navy-2" /> Users</span>
                <span className="flex items-center gap-2 text-[11px] text-propertic-muted font-mono-brand"><span className="w-3 h-3 rounded-sm bg-gradient-to-t from-propertic-gold-2 to-propertic-gold" /> Bot crawls</span>
              </div>
            </section>
            <section className="bg-white rounded-2xl border hairline p-5 sm:p-6" data-testid="top-countries-card">
              <div className="flex items-center gap-2 mb-4">
                <Globe2 className="w-4 h-4 text-propertic-gold" />
                <h2 className="font-editorial text-base sm:text-xl text-propertic-navy">Top countries (call intent)</h2>
              </div>
              {(data.top_countries || []).length === 0 && <p className="text-sm text-propertic-muted">No clicks yet.</p>}
              <div className="space-y-2">
                {(data.top_countries || []).map((c) => {
                  const iso = flag(c.country);
                  const max = data.top_countries[0].count || 1;
                  const pct = Math.max(4, Math.round((c.count / max) * 100));
                  return (
                    <div key={c.country} className="flex items-center gap-3" data-testid={`country-${c.country}`}>
                      {iso ? <img src={`https://flagcdn.com/w40/${iso}.png`} alt="" width="20" height="15" className="w-5 h-auto rounded-[2px] shadow-sm" /> : <Globe2 className="w-4 h-4 text-propertic-muted" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-propertic-navy truncate">{c.country}</p>
                          <p className="font-mono-brand text-[11px] text-propertic-muted tabular">{c.count}</p>
                        </div>
                        <div className="mt-1 h-1.5 bg-propertic-cream rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-propertic-gold-2 to-propertic-gold" style={{ width: `${pct}%` }} /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <section className="bg-white rounded-2xl border hairline overflow-hidden" data-testid="recent-clicks-card">
            <div className="px-5 sm:px-6 py-4 border-b hairline flex items-center justify-between gap-2">
              <h2 className="font-editorial text-base sm:text-xl text-propertic-navy">Recent call intent · {data.range_label}</h2>
              <span className="hidden sm:inline text-[11px] text-propertic-muted font-mono-brand">time · type · country</span>
            </div>
            {(data.recent_clicks || []).length === 0 ? (
              <p className="p-6 text-sm text-propertic-muted">No clicks in this range. Change filter or try the WhatsApp / Call buttons on the site.</p>
            ) : (
              <>
                {/* Mobile: card list. Cleaner than a cramped table. */}
                <ul className="md:hidden divide-y hairline">
                  {data.recent_clicks.map((r, i) => {
                    const iso = flag(r.country);
                    return (
                      <li key={i} className="px-5 py-3 flex items-center gap-3" data-testid={`click-row-m-${i}`}>
                        {iso ? <img src={`https://flagcdn.com/w40/${iso}.png`} alt="" width="24" height="18" className="w-6 h-auto rounded-[2px] shadow-sm flex-shrink-0" /> : <Globe2 className="w-4 h-4 text-propertic-muted flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <ClickTypeBadge type={r.type} />
                            <span className="text-[11px] text-propertic-navy truncate">{r.country || "Unknown"}</span>
                          </div>
                          <p className="text-[10px] text-propertic-muted font-mono-brand">{new Date(r.created_at).toLocaleString()}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop: full table */}
                <table className="hidden md:table w-full text-sm">
                  <thead className="text-propertic-muted bg-propertic-cream/40">
                    <tr>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase">When</th>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase">Type</th>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase">Country</th>
                      <th className="text-start px-6 py-3 font-mono-brand text-[10px] tracking-[0.14em] uppercase">Path</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {data.recent_clicks.map((r, i) => (
                      <tr key={i} className="hover:bg-propertic-cream/40" data-testid={`click-row-${i}`}>
                        <td className="px-6 py-3 text-propertic-navy font-mono-brand text-[11px] whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                        <td className="px-6 py-3"><ClickTypeBadge type={r.type} /></td>
                        <td className="px-6 py-3 text-propertic-ink">{r.country || "Unknown"}</td>
                        <td className="px-6 py-3 text-propertic-muted text-[12px] truncate max-w-[240px]">{r.path}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, range, testid, accent, to }) {
  const inner = (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.12em] uppercase leading-tight">{label}</p>
        <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" style={{ color: accent || "#B08543" }} />
      </div>
      <p className="font-editorial text-3xl sm:text-4xl mt-2 sm:mt-3 text-propertic-navy tabular">{value}</p>
      {range && <p className="text-[10px] sm:text-[11px] text-propertic-muted mt-1 font-mono-brand truncate">{range}</p>}
      {to && (
        <p className="mt-3 text-[9px] sm:text-[10px] font-mono-brand tracking-[0.14em] uppercase text-propertic-gold group-hover:text-propertic-navy transition-colors">
          Open log →
        </p>
      )}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testid}
        className="group block bg-white rounded-2xl border hairline p-4 sm:p-5 hover:border-propertic-gold hover:shadow-sm transition-all"
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="bg-white rounded-2xl border hairline p-4 sm:p-5" data-testid={testid}>
      {inner}
    </div>
  );
}

function ClickTypeBadge({ type }) {
  const map = {
    whatsapp: { cls: "text-emerald-700 border-emerald-300 bg-emerald-50", label: "WhatsApp" },
    call: { cls: "text-propertic-gold border-propertic-gold/30 bg-propertic-cream", label: "Call" },
    roni_install_cta: { cls: "text-propertic-navy border-propertic-navy/20 bg-propertic-navy/5", label: "Roni install" },
  };
  const cfg = map[type] || { cls: "text-propertic-muted border-propertic-muted/30 bg-white", label: type };
  return (
    <span className={`font-mono-brand text-[9px] sm:text-[10px] tracking-[0.12em] uppercase border rounded-full px-2 py-0.5 whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function DailyBars({ series }) {
  const max = Math.max(1, ...series.map((s) => (s.users || 0) + (s.bots || 0)));
  return (
    <div className="flex items-end justify-between gap-[3px] sm:gap-1.5 h-32 sm:h-40" data-testid="daily-bars">
      {series.map((s) => {
        const total = (s.users || 0) + (s.bots || 0);
        const userH = Math.max(0, Math.round(((s.users || 0) / max) * 100));
        const botH = Math.max(0, Math.round(((s.bots || 0) / max) * 100));
        const label = new Date(s.day + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short" });
        return (
          <div key={s.day} className="flex-1 flex flex-col items-center gap-1 min-w-0" title={`${s.day}: ${s.users || 0} users · ${s.bots || 0} bots`}>
            <div className="w-full flex flex-col items-center justify-end h-full">
              <span className="text-[8px] sm:text-[10px] text-propertic-muted font-mono-brand tabular mb-1">{total}</span>
              <div className="w-full flex flex-col justify-end" style={{ height: "100%" }}>
                {s.bots > 0 && <div className="w-full bg-gradient-to-t from-propertic-gold-2 to-propertic-gold" style={{ height: `${botH}%`, minHeight: 2 }} />}
                {s.users > 0 && <div className="w-full rounded-t-lg bg-gradient-to-t from-propertic-navy to-propertic-navy-2" style={{ height: `${userH}%`, minHeight: 2 }} />}
              </div>
            </div>
            <span className="text-[8px] sm:text-[10px] text-propertic-muted font-mono-brand uppercase tracking-[0.1em] truncate w-full text-center">{label.slice(0, 3)}</span>
          </div>
        );
      })}
    </div>
  );
}
